import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

/**
 * Interceptor de autenticación mejorado con manejo automático de refresh tokens
 * Siguiendo principios SOLID:
 * - Single Responsibility: Solo maneja autenticación en requests HTTP
 * - Open/Closed: Extensible para nuevos tipos de autenticación
 * - Dependency Inversion: Depende de abstracciones (AuthService)
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  
  // ==================== ESTADO PRIVADO ====================
  
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private failedRequestsCount = 0;
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly CONCURRENT_REQUEST_LIMIT = 5;
  
  // Lista de requests en cola esperando token refresh
  private pendingRequests: Array<{
    request: HttpRequest<any>;
    next: HttpHandler;
  }> = [];

  private authService!: AuthService;
  private toastService!: ToastService;

  constructor(
    private injector: Injector
  ) {
    console.log('AuthInterceptor mejorado inicializado');
  }

  // ==================== MÉTODOS DE SERVICIO ====================

  /**
   * Obtiene AuthService usando inyección manual para evitar dependencias circulares
   */
  private getAuthService(): AuthService {
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
    return this.authService;
  }

  /**
   * Obtiene ToastService usando inyección manual
   */
  private getToastService(): ToastService {
    if (!this.toastService) {
      this.toastService = this.injector.get(ToastService);
    }
    return this.toastService;
  }

  // ==================== MÉTODO PRINCIPAL ====================

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Verificar si es un endpoint de autenticación
    if (this.isAuthEndpoint(req.url)) {
      return next.handle(req);
    }

    // Obtener token y agregarlo si está disponible
    const token = this.getAuthService().getAccessToken();
    
    if (token && this.getAuthService().isAuthenticated()) {
      req = this.addTokenToRequest(req, token);
    }

    // Manejar la request con retry automático
    return this.handleRequestWithRetry(req, next);
  }

  // ==================== MANEJO DE REQUESTS ====================

  /**
   * Maneja la request con lógica de retry automático
   */
  private handleRequestWithRetry(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Solo manejar errores 401 para endpoints no-auth
        if (error.status === 401 && !this.isAuthEndpoint(req.url)) {
          return this.handle401Error(req, next);
        }
        
        // Para otros errores, propagar directamente
        return throwError(() => error);
      })
    );
  }

  /**
   * Maneja errores 401 con refresh token automático
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.warn('Error 401 detectado para:', request.url);
    
    // Verificar límite de requests fallidos
    if (this.failedRequestsCount >= this.MAX_RETRY_ATTEMPTS) {
      console.error('Límite de reintentos alcanzado, cerrando sesión');
      this.resetState();
      this.getAuthService().logoutLocal();
      this.showSessionExpiredMessage();
      return throwError(() => new Error('Max retry attempts reached'));
    }

    // Verificar si tenemos refresh token
    const refreshToken = this.authService.getRefreshToken();
    if (!refreshToken) {
      console.warn('No hay refresh token disponible, cerrando sesión');
      this.getAuthService().logoutLocal();
      this.showSessionExpiredMessage();
      return throwError(() => new Error('No refresh token available'));
    }

    // Manejar refresh token concurrente
    return this.handleConcurrentRefresh(request, next);
  }

  /**
   * Maneja refresh tokens concurrentes para evitar múltiples llamadas simultáneas
   */
  private handleConcurrentRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      // Iniciar proceso de refresh
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      this.failedRequestsCount++;

      console.log('Iniciando refresh de token automático...');

      return this.authService.refreshAccessToken().pipe(
        switchMap((response: any) => {
          console.log('Refresh automático exitoso');
          
          // Resetear estado
          this.isRefreshing = false;
          this.resetFailedRequestsCount();
          this.refreshTokenSubject.next(response.accessToken);
          
          // Reintentar request original con nuevo token
          const newRequest = this.addTokenToRequest(request, response.accessToken);
          return next.handle(newRequest);
        }),
        catchError((error) => {
          console.error('Error en refresh automático:', error);
          
          // Manejar error de refresh
          this.isRefreshing = false;
          this.handleRefreshError(error);
          
          return throwError(() => error);
        }),
        finalize(() => {
          // Procesar requests en cola
          this.processPendingRequests();
        })
      );

    } else {
      // Ya se está refrescando, agregar a cola o esperar
      if (this.pendingRequests.length < this.CONCURRENT_REQUEST_LIMIT) {
        console.log('Agregando request a cola de espera...');
        this.pendingRequests.push({ request, next });
      }

      // Esperar a que termine el refresh
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          const newRequest = this.addTokenToRequest(request, token);
          return next.handle(newRequest);
        })
      );
    }
  }

  /**
   * Procesa requests en cola después del refresh
   */
  private processPendingRequests(): void {
    if (this.pendingRequests.length > 0) {
      console.log(`Procesando ${this.pendingRequests.length} requests en cola...`);
      
      const token = this.authService.getAccessToken();
      if (token) {
        this.pendingRequests.forEach(({ request, next }) => {
          const newRequest = this.addTokenToRequest(request, token);
          next.handle(newRequest).subscribe(); // Procesar en background
        });
      }
      
      this.pendingRequests = []; // Limpiar cola
    }
  }

  /**
   * Maneja errores de refresh token
   */
  private handleRefreshError(error: any): void {
    console.error('Refresh token falló:', error);
    
    // Determinar tipo de error
    if (error.status === 401 || error.status === 403) {
      // Refresh token inválido o expirado
      console.warn('Refresh token inválido, cerrando sesión');
      this.getAuthService().logoutLocal();
      this.showSessionExpiredMessage();
    } else if (error.status === 0) {
      // Error de red
      console.warn('Error de red en refresh token');
      this.showNetworkErrorMessage();
    } else {
      // Otros errores del servidor
      console.warn('Error del servidor en refresh token');
    }
    
    this.resetState();
  }

  // ==================== MÉTODOS UTILITARIOS ====================

  /**
   * Verifica si la URL es un endpoint de autenticación
   */
  private isAuthEndpoint(url: string): boolean {
    const authEndpoints = [
      '/auth/login', 
      '/auth/register', 
      '/auth/logout', 
      '/auth/refresh',
      '/auth/forgot-password',
      '/auth/reset-password'
    ];
    
    return authEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Agrega el token de autorización a la request
   */
  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  /**
   * Resetea el contador de requests fallidas
   */
  private resetFailedRequestsCount(): void {
    this.failedRequestsCount = 0;
  }

  /**
   * Resetea todo el estado del interceptor
   */
  private resetState(): void {
    this.isRefreshing = false;
    this.failedRequestsCount = 0;
    this.pendingRequests = [];
    this.refreshTokenSubject.next(null);
  }

  // ==================== MÉTODOS DE NOTIFICACIÓN ====================

  /**
   * Muestra mensaje de sesión expirada
   */
  private showSessionExpiredMessage(): void {
    this.toastService.showToast('warning', 
      'Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
  }

  /**
   * Muestra mensaje de error de red
   */
  private showNetworkErrorMessage(): void {
    this.toastService.showToast('error', 
      'Error de conexión. Verifique su conexión a internet.');
  }

  /**
   * Muestra mensaje de refresh exitoso
   */
  private showRefreshSuccessMessage(): void {
    this.toastService.showToast('success', 
      'Sesión renovada automáticamente');
  }

  // ==================== MÉTODOS DE DESARROLLO/DEBUG ====================

  /**
   * Obtiene estadísticas del interceptor (útil para debugging)
   */
  public getInterceptorStats(): {
    isRefreshing: boolean;
    failedRequestsCount: number;
    pendingRequestsCount: number;
    maxRetryAttempts: number;
  } {
    return {
      isRefreshing: this.isRefreshing,
      failedRequestsCount: this.failedRequestsCount,
      pendingRequestsCount: this.pendingRequests.length,
      maxRetryAttempts: this.MAX_RETRY_ATTEMPTS
    };
  }

  /**
   * Fuerza el reset del estado (útil para testing)
   */
  public forceReset(): void {
    console.log('Forzando reset del AuthInterceptor');
    this.resetState();
  }
}
